import ApplicationDetail from 'domain/types/ApplicationDetail';
import { useContext, useState } from 'react';
import useThunkDispatch from 'common/hooks/useThunkDispatch';
import Modal from 'common/components/modal/Modal';
import useApi from 'common/hooks/useApi';
import useTypedSelector from 'common/hooks/useTypedSelector';
import { ActionCompleteResponse } from 'common/types/ApiResponse';
import ClientFacingStatus from 'domain/enums/ClientFacingStatus';
import { StatusAlert } from 'features/group-details/StatusAlert';
import { addToCart } from 'features/cart/CartSlice';
import { Button } from 'react-bootstrap';
import AlertRegistration from './AlertRegistration';
import { GroupDetailsContext } from 'features/group-details/GroupDetailsContainer';
import { GroupDetailForm } from 'features/group-details/GroupDetailForm';
import { ApplicationStatus } from 'domain/components/application/ApplicationStatus';
import RejectionStatusCd from 'domain/enums/RejectionStatusCd';

interface IRegistrationDetails {
  applicationDetails: ApplicationDetail;
  onRefresh: () => Promise<unknown>;
}

const RegistrationDetailsPresenter = (props: IRegistrationDetails) => {
  const { applicationDetails } = props;
  const dispatch = useThunkDispatch();
  const { groupRefKeys } = useTypedSelector((state) => ({
    groupRefKeys: state.cart.groupRefKeys,
  }));
  const [resubmitVisible, setResubmitVisible] = useState(false);
  const [{ loading: resubmitting }, resubmit] = useApi<ActionCompleteResponse>({
    url: `/applications/${props.applicationDetails.id}/resubmit`,
    method: 'POST',
  });

  const groupDetailsContext = useContext(GroupDetailsContext);

  const allResolved = applicationDetails.rejectionReasons.every(
    (r) => r.statusCd !== RejectionStatusCd.NOT_RESOLVED
  );

  function addAppToCart(id: string) {
    dispatch(addToCart(id));
  }

  return (
    <>
      <AlertRegistration data={props.applicationDetails} />
      <StatusAlert
        clientFacingStatusCd={applicationDetails.clientFacingStatusCd}
        rejections={applicationDetails.rejectionReasons}
        groupRefKey={applicationDetails.groupRefKey}
        shouldCheckout={
          applicationDetails.clientFacingStatusCd ===
            ClientFacingStatus.APPROVED_PAYMENT_REQUIRED &&
          !groupRefKeys.includes(applicationDetails.groupRefKey)
        }
        onCheckout={() => addAppToCart(applicationDetails.groupRefKey)}
        canRequestRenewal={
          applicationDetails.clientFacingStatusCd ===
          ClientFacingStatus.READY_FOR_RENEWAL
        }
      />
      <Modal
        show={resubmitVisible}
        title={
          <h3>
            {applicationDetails.intermediaries.length > 1
              ? `Resubmit ${applicationDetails.intermediaries.length}
                    applications?`
              : `Resubmit application?`}
          </h3>
        }
        body={
          <>
            <p>
              {applicationDetails.intermediaries.length > 1
                ? `You are about to resubmit this application along with ${
                    applicationDetails.intermediaries.length - 1
                  }
                    related applications. Would you like to Continue?`
                : `You are about to resubmit one application. Would you like to Continue?`}
            </p>
            <div className='d-flex'>
              <Button
                variant='outline-secondary'
                className='mr-auto'
                onClick={() => setResubmitVisible(false)}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={() =>
                  resubmit().then(() => {
                    groupDetailsContext.onRefresh();
                    setResubmitVisible(false);
                  })
                }
                disabled={resubmitting}
              >
                Resubmit
              </Button>
            </div>
          </>
        }
      />
      <GroupDetailForm
        details={props.applicationDetails}
        intermediaries={props.applicationDetails.intermediaries}
        canResubmit={
          props.applicationDetails.appStatusCd ===
            ApplicationStatus.PRECHECK_FAILED && allResolved
        }
        onResubmitClick={() => setResubmitVisible(true)}
      />
    </>
  );
};

export default RegistrationDetailsPresenter;
